import { Icon, spacing, Text } from '@scality/core-ui';
import type React from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  mergeRefs,
  SelectField,
  SelectFieldLabel,
  SelectFieldText,
  SelectFieldTrigger,
  SelectMenuPanel,
  SelectMenuPlainList,
  SelectMenuRow,
  SelectWrapper,
  useFloatingSelectMenu,
} from '../../../components/select';
import { AbsoluteRangeForm, timeRangeBounds } from './AbsoluteRangeForm';
import { timeRangeLabel, timeRangeOptions } from './timeRangePresets';

const Panel = styled(SelectMenuPanel)`
  flex-direction: row;
  flex-wrap: wrap;
  max-width: min(36rem, calc(100vw - 2rem));
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
`;

const QuickRanges = styled.div`
  flex: 1 1 13rem;
  min-width: 13rem;
  padding: ${spacing.r8} ${spacing.r4};
  border-right: 1px solid ${(p) => p.theme.border};
`;

const ColumnTitle = styled.div`
  padding: 0 ${spacing.r8} ${spacing.r4};
`;

/** About as tall as the absolute range beside it, so the panel stays compact and the list scrolls. */
const QuickRangeList = styled(SelectMenuPlainList)`
  max-height: 13rem;
  overflow-y: auto;
`;

const QuickRangeButton = styled(SelectMenuRow).attrs({ as: 'button', type: 'button' })`
  width: 100%;
  border: none;
  font: inherit;
  text-align: left;

  &:focus {
    outline: none;
  }
  /* Inset: the scrolling list clips anything drawn outside a row. */
  &:focus-visible {
    outline: dashed ${spacing.r2} ${(p) => p.theme.selectedActive};
    outline-offset: -${spacing.r2};
  }
`;

type TimeRangeControlProps = {
  /** Staged value, or `undefined` to fall back to `defaultValue`. */
  value: string | undefined;
  /** The template's default range — shown as current when nothing is staged. */
  defaultValue: string | undefined;
  onChange: (value: string) => void;
};

/**
 * The period a dashboard is read over: a quick range, or two bounds picked in an absolute range.
 * A popover rather than a select, since a select closes as soon as focus leaves its trigger for
 * the date fields.
 *
 * The quick ranges include the applied one even when it is not a preset, so the control can never
 * disagree with the URL.
 */
export const TimeRangeControl: React.FC<TimeRangeControlProps> = ({ value, defaultValue, onChange }) => {
  const active = value ?? defaultValue;
  const labelId = useId();
  const valueId = useId();
  const panelId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const options = useMemo(() => timeRangeOptions(defaultValue, active), [defaultValue, active]);
  const activeLabel = active ? timeRangeLabel(active) : '—';

  const { refs: floatingRefs, floatingStyles } = useFloatingSelectMenu({
    isOpen,
    preferredBelow: true,
    mainAxisAbovePx: 8,
    mainAxisBelowPx: 8,
  });

  const closeToTrigger = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    (
      panel?.querySelector<HTMLElement>('[aria-pressed="true"]') ?? panel?.querySelector<HTMLElement>('button')
    )?.focus();

    const isInside = (node: Node) => !!triggerRef.current?.contains(node) || !!panelRef.current?.contains(node);
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!isInside(event.target as Node)) setIsOpen(false);
    };
    // No related target: focus left for the browser's own date picker or another window.
    const closeOnFocusLeaving = (event: FocusEvent) => {
      if (event.relatedTarget instanceof Node && !isInside(event.relatedTarget)) setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeToTrigger();
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    panel?.addEventListener('focusout', closeOnFocusLeaving);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
      panel?.removeEventListener('focusout', closeOnFocusLeaving);
    };
  }, [isOpen, closeToTrigger]);

  const pickRange = (range: string) => {
    onChange(range);
    closeToTrigger();
  };

  return (
    <SelectField>
      <SelectFieldLabel id={labelId} variant="Basic" color="textSecondary">
        Period
      </SelectFieldLabel>
      <SelectWrapper $truncate>
        <SelectFieldTrigger
          type="button"
          ref={mergeRefs(floatingRefs.setReference, triggerRef)}
          aria-labelledby={`${labelId} ${valueId}`}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-controls={panelId}
          title={activeLabel}
          onClick={() => setIsOpen((open) => !open)}
          $isOpen={isOpen}
        >
          <SelectFieldText id={valueId}>{activeLabel}</SelectFieldText>
          <Icon name="ArrowsUpDown" size="xs" />
        </SelectFieldTrigger>
        <Panel
          id={panelId}
          ref={mergeRefs(floatingRefs.setFloating, panelRef)}
          role="dialog"
          aria-labelledby={labelId}
          aria-hidden={!isOpen}
          style={floatingStyles}
          $isOpen={isOpen}
        >
          {isOpen && (
            <>
              <QuickRanges>
                <ColumnTitle>
                  <Text variant="Smaller" color="textSecondary">
                    Quick ranges
                  </Text>
                </ColumnTitle>
                <QuickRangeList>
                  {options.map((option) => (
                    <li key={option}>
                      <QuickRangeButton
                        $highlighted={option === active}
                        aria-pressed={option === active}
                        onClick={() => pickRange(option)}
                      >
                        <Text>{timeRangeLabel(option)}</Text>
                        {option === active && <Icon name="Check" size="sm" />}
                      </QuickRangeButton>
                    </li>
                  ))}
                </QuickRangeList>
              </QuickRanges>
              <AbsoluteRangeForm initialRange={timeRangeBounds(active, new Date())} onApply={pickRange} />
            </>
          )}
        </Panel>
      </SelectWrapper>
    </SelectField>
  );
};
